import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Code2,
  Network,
  ShieldCheck,
  Users,
} from "lucide-react";

/*
 * Faculty details are intentionally kept easy to edit.
 * Replace the placeholder cards below with the confirmed teacher
 * names, roles, photos and bios before the batch is announced.
 */
const instructors = [
  {
    name: "Cyber Security Instructor",
    role: "Lead Faculty",
    icon: ShieldCheck,
    focus: "Cyber Security & Ethical Hacking",
    bio: "Practical guidance across cybersecurity fundamentals, security concepts and responsible hands-on learning.",
  },
  {
    name: "Technical Instructor",
    role: "Technical Faculty",
    icon: Network,
    focus: "Networking & Security Labs",
    bio: "Hands-on learning around networking, security tools, lab exercises and practical problem solving.",
  },
  {
    name: "Application Security Instructor",
    role: "Technical Faculty",
    icon: Code2,
    focus: "Web & Application Security",
    bio: "Guided learning around web security, application testing and secure development concepts.",
  },
];

export const FacultyPage = () => (
  <main className="min-h-screen bg-background text-foreground">
    <section className="relative overflow-hidden border-b border-border/40 py-16 md:py-24">
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono uppercase tracking-wider">
            Kian Technologies • Masterclass Faculty
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Learn From Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              Cyber Security Faculty
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            The 15-day Masterclass will be delivered by a team of cybersecurity
            instructors with a focus on practical learning, guided labs and
            responsible security practices.
          </p>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-5 mb-10">
          <div>
            <span className="text-xs font-mono uppercase tracking-wider text-emerald-400">
              Masterclass Instructors
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold mt-2">
              Your Learning Team
            </h2>
          </div>
          <Users className="hidden sm:block w-8 h-8 text-emerald-400" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {instructors.map((person) => {
            const Icon = person.icon;
            return (
              <article
                key={person.name}
                className="p-6 rounded-2xl bg-card border border-border hover:border-emerald-500/40 transition-all shadow-sm"
              >
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-emerald-400" />
                </div>
                <div className="mt-5">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-cyan-400">
                    {person.role}
                  </span>
                  <h3 className="text-xl font-bold mt-1">{person.name}</h3>
                  <p className="text-xs text-emerald-400 mt-2 font-mono">
                    {person.focus}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-4">
                    {person.bio}
                  </p>
                </div>
                <div className="mt-5 pt-4 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Practical, guided learning
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-10 p-5 rounded-xl border border-border bg-muted/20 text-sm text-muted-foreground">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              Faculty names, photographs and individual profiles can be updated
              here once the final teaching team is confirmed. No student-facing
              functionality depends on these profiles.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-14 border-t border-border/40 text-center bg-gradient-to-t from-emerald-950/20 to-transparent">
      <div className="max-w-2xl mx-auto px-4">
        <h2 className="text-2xl font-bold">Ready to learn?</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Explore the 15-day curriculum and apply for the Masterclass.
        </p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-sm transition-colors"
        >
          Apply for the Masterclass
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  </main>
);
